import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import BasicList from 'core/modules/components/lists/components/BasicList/BasicList';
import CalendarItem from 'core/modules/components/lists/calendars/CalendarItem/CalendarItem';
import { isCourseReadOnly, isSchoolProgram } from 'core/utils/course';
import React, { useEffect, useRef } from 'react';
import Icon from '../../../atoms/Icon/Icon';

/**
 * CalendarsList - Lista de calendarios de cursos. Envuelve el genérico v2 `BasicList`
 * (semántica `<ul>/<li>` accesible) y, opcionalmente, agrupa por colegio con acordeones.
 */

// `CalendarItem` es JS (props sin tipar): se aliasa a un componente permisivo para poder
// inyectar las props de dominio de cada calendario mediante spread.
const CalendarRow = CalendarItem as unknown as React.FC<Record<string, unknown>>;

export type Calendar = Readonly<{
  guid?: string;
  theme_color?: string;
  theme_image?: string;
  course_guid?: string;
}> &
  Record<string, unknown>;

export type CalendarGroup = Readonly<{
  guid: string;
  name: string;
  calendars: Calendar[];
}>;

type CalendarsListProps = Readonly<{
  calendars?: Calendar[];
  calendarsShows?: string[];
  configProgramPicture?: boolean;
  getVisibilityLabel?: (courseName: string) => string;
  onClickVisible?: (...args: unknown[]) => void;
  onClickVisibleGroup?: (calendars: Calendar[]) => void;
  onConfigureCourse?: (calendar: Calendar) => void;
  schoolGroupsCalendars?: CalendarGroup[] | null;
  showOptions?: boolean;
}>;

type GroupVisibilityToggleProps = Readonly<{
  group: CalendarGroup;
  label: string;
  onToggle: (calendars: Calendar[]) => void;
  shownCount: number;
}>;

/**
 * Toggle de visibilidad de un grupo entero. Casilla nativa —no `<button aria-pressed>`— porque
 * VoiceOver no pronuncia el estado apagado de un toggle button; el icono es el visual, como hace
 * el propio `Switch` del design system.
 */
function GroupVisibilityToggle({ group, label, onToggle, shownCount }: GroupVisibilityToggleProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const isVisible = group.calendars.length > 0 && shownCount === group.calendars.length;
  const isIndeterminate = shownCount > 0 && shownCount < group.calendars.length;

  // `every()` no distingue "todo visible" de "la mitad visible", y anunciar "no marcada" con parte
  // del grupo en pantalla es un estado programático falso. `indeterminate` es lo que existe para
  // esto, y solo se puede poner desde el DOM.
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  function handleToggle(): void {
    onToggle(group.calendars);
  }

  // A native checkbox only toggles with Space, and the criteria ask for Enter too. `repeat` guards
  // against the OS key autorepeat sending one toggle per keydown while Enter is held down.
  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key !== 'Enter' || event.repeat) return;
    event.preventDefault();
    handleToggle();
  }

  return (
    <label className="calendar-item__visible-icon">
      <input aria-label={label} checked={isVisible} className="sr-only" onChange={handleToggle} onKeyDown={handleKeyDown} ref={inputRef} type="checkbox" />
      <Icon size="md" name={isVisible ? 'visible' : 'visible-off'} />
    </label>
  );
}

function CalendarsList({
  calendars = [],
  calendarsShows = [],
  configProgramPicture = false,
  getVisibilityLabel,
  onClickVisible = () => {},
  onClickVisibleGroup = () => {},
  onConfigureCourse = () => {},
  schoolGroupsCalendars = null,
  showOptions = true,
}: CalendarsListProps): JSX.Element {
  function countShown(groupCalendars: Calendar[]): number {
    return groupCalendars.filter((calendar) => calendarsShows.includes(calendar.guid ?? '')).length;
  }

  if (schoolGroupsCalendars?.length) {
    return (
      <div className="calendars-list">
        {schoolGroupsCalendars.map((group) => (
          <div className="calendars-list__group-row" key={group.guid}>
            {/* Hermano del disparador del acordeón, no descendiente: `AccordionSummary` es un
                ButtonBase con `role="button"`, y una casilla enfocable dentro de él es
                `nested-interactive` (WCAG 4.1.2) y además se cuela en su nombre accesible.
                Va antes del acordeón porque el orden de tabulación es el del DOM: detrás, el foco
                llegaba al toggle del grupo después de todos sus cursos (WCAG 2.4.3). La rejilla de
                `&__group-row` lo coloca igual en la cabecera, sea cual sea el orden del DOM. */}
            <div className="calendars-list__group-options-wrapper">
              <GroupVisibilityToggle group={group} label={group.name} onToggle={onClickVisibleGroup} shownCount={countShown(group.calendars)} />
            </div>
            <Accordion className="calendars-list__group" defaultExpanded={true}>
              <AccordionSummary expandIcon={<Icon name="chevron-down" />} aria-controls={`${group.guid}-content`} id={`${group.guid}-header`}>
                <div className="calendars-list__group-summary">
                  <div className="calendars-list__group-name">{group.name}</div>
                </div>
              </AccordionSummary>
              <AccordionDetails className="calendars-list__group-details">
                <BasicList
                  items={group.calendars.map((item) => (
                    <CalendarRow
                      {...item}
                      key={item?.guid}
                      themeColor={item.theme_color}
                      themeImage={item.theme_image}
                      visible={calendarsShows.includes(item.guid)}
                      getVisibilityLabel={getVisibilityLabel}
                      onClickVisible={onClickVisible}
                      showOptions={showOptions && !isSchoolProgram(item) && !isCourseReadOnly(item)}
                      onConfigureCourse={() => onConfigureCourse(item)}
                      configProgramPicture={configProgramPicture}
                    />
                  ))}
                />
              </AccordionDetails>
            </Accordion>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="calendars-list">
      <BasicList
        items={calendars.map((item) => (
          <CalendarRow
            {...item}
            key={item?.guid}
            themeColor={item.theme_color}
            themeImage={item.theme_image}
            visible={calendarsShows.includes(item.guid)}
            getVisibilityLabel={getVisibilityLabel}
            onClickVisible={onClickVisible}
            showOptions={showOptions && !isSchoolProgram(item) && !isCourseReadOnly(item)}
            onConfigureCourse={() => onConfigureCourse(item)}
            configProgramPicture={configProgramPicture}
            showGroupName={true}
          />
        ))}
      />
    </div>
  );
}

export default CalendarsList;
